/**
 * Rejects with an AbortError the moment `signal` aborts, otherwise settles with `promise`. Used to
 * abandon a shared WAF clearance wait when the caller (a stopped ai-chat send) gives up — it only drops
 * this waiter, never cancelling the in-flight solve for peers still waiting on it. A missing signal is a
 * no-op passthrough, so the axios path (which passes no signal) pays nothing. The losing side of the race is
 * discarded, so callers must not pass a `promise` whose rejection needs handling after an abort — the WAF
 * callers only pass a promise that resolves (the clearance deferred).
 */
export function raceAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) {
    return promise;
  }
  let onAbort: (() => void) | undefined;
  const abortPromise = new Promise<never>((_resolve, reject) => {
    if (signal.aborted) {
      reject(makeAbortError());
      return;
    }
    onAbort = () => reject(makeAbortError());
    signal.addEventListener('abort', onAbort, { once: true });
  });
  return Promise.race([promise, abortPromise]).finally(() => {
    if (onAbort) {
      signal.removeEventListener('abort', onAbort);
    }
  });
}

/**
 * Throws an AbortError if the signal is already aborted; a no-op otherwise. Used to bail before doing work
 * (minting a clearance, emitting telemetry) for a request the caller already abandoned.
 */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw makeAbortError();
  }
}

// The AI SDK maps a rejection whose `name` is 'AbortError' to a clean stop; a DOMException matches the
// app's other abort sites (async.utils.ts, BrowserDownloader.ts, filmstripCapture.ts).
const makeAbortError = (): DOMException => new DOMException('WAF recovery aborted', 'AbortError');
